import {
  listParamsFromNavNode,
  type GameListParams,
  type GameNavNode,
  type GameSummary,
} from '@/features/game/game-api';
import { ApiBusinessError } from '@/shared/api/result';
import { t } from '@/shared/i18n/messages';
import {
  oroMxAssetPaths,
  oroMxVenueCatalog,
  resolveOroMxCoverUrl,
  resolveOroMxPublicUrl,
  type OroMxGame,
  type OroMxTab,
} from '@/shared/theme/oro-mx-home-data';

export const ORO_MX_CHROME_TAB_IDS = ['lobby', 'estudios', 'favoritos'] as const;

const TAB_ALIASES: Record<string, string> = {
  fish: 'fishing',
  live: 'live_casino',
  pesca: 'fishing',
  slots: 'slot',
  sports: 'sportsbook',
};

const CATALOG_LABEL_KEYS: Record<string, string> = {
  fishing: 'oro.lobby.fishing',
  live_casino: 'oro.lobby.live',
  nuevos: 'oro.lobby.nuevos',
  popular: 'oro.lobby.popular',
  'popular-alt': 'oro.lobby.popular-alt',
  slot: 'oro.lobby.slots',
  sportsbook: 'oro.lobby.sports',
};

export const toOroMxGame = (game: GameSummary): OroMxGame => ({
  id: game.gameCode,
  image: resolveOroMxCoverUrl(game.coverUrl) || game.coverUrl || '',
  title: game.title,
  venueCode: game.venueCode || undefined,
});

/** 源站 Lobby 在场馆条下固定三列合集；只兜底导航结构，不铺不可进游的源站卡片。 */
export const ORO_MX_FALLBACK_COLLECTIONS: GameNavNode[] = [
  {
    children: [],
    count: 0,
    iconUrl: oroMxAssetPaths.popular,
    key: 'collection-popular',
    label: 'Popular',
    refCode: 'popular',
    type: 'COLLECTION',
  },
  {
    children: [],
    count: 0,
    iconUrl: oroMxAssetPaths.popularAlt,
    key: 'collection-popular-alt',
    label: 'Popular',
    refCode: 'popular-alt',
    type: 'COLLECTION',
  },
  {
    children: [],
    count: 0,
    iconUrl: oroMxAssetPaths.nuevos,
    key: 'collection-nuevos',
    label: 'Nuevos',
    refCode: 'nuevos',
    type: 'COLLECTION',
  },
];

/** 进游失败文案：目录没有该游戏、可用余额不足 1，其余视为厂商未开放。 */
export const resolveOroMxLaunchErrorKey = (error: unknown): string => {
  if (error instanceof ApiBusinessError && error.code === 'GAME_NOT_FOUND') {
    return 'oro.game.notFound';
  }
  if (error instanceof ApiBusinessError && error.code === 'GAME_BALANCE_TOO_LOW') {
    return 'oro.game.balanceTooLow';
  }
  return 'oro.game.unavailable';
};

export const withFallbackCollections = (nodes: GameNavNode[]): GameNavNode[] => {
  const catalog = catalogNavNodes(nodes);
  if (catalog.some((node) => node.type === 'COLLECTION')) {
    return catalog;
  }
  return [...ORO_MX_FALLBACK_COLLECTIONS, ...catalog];
};

/** Lobby 合集头：无导航时也先铺 Popular / Nuevos，不跟 /home 接口。 */
export const displayLobbyCollectionNodes = (nodes: GameNavNode[]): GameNavNode[] =>
  withFallbackCollections(nodes).filter((node) => node.type === 'COLLECTION');

export const isOroMxChromeTab = (id: string) =>
  (ORO_MX_CHROME_TAB_IDS as readonly string[]).includes(id);

export const flattenGameNav = (nodes: GameNavNode[]): GameNavNode[] => {
  const out: GameNavNode[] = [];
  for (const node of nodes) {
    out.push(node);
    if (node.children?.length) {
      out.push(...flattenGameNav(node.children));
    }
  }
  return out;
};

export const catalogNavNodes = (nodes: GameNavNode[]) =>
  flattenGameNav(nodes).filter((node) => node.type === 'CATEGORY' || node.type === 'COLLECTION');

export const venueNavNodes = (nodes: GameNavNode[]) =>
  flattenGameNav(nodes).filter((node) => node.type === 'VENUE');

/** 场馆条上的第二枚 PG Soft logo 仍筛 PG 游戏。 */
const VENUE_GAME_ALIASES: Record<string, string> = { PGS: 'PG' };

export const resolveVenueGameCode = (venueCode: string) =>
  VENUE_GAME_ALIASES[venueCode] ?? venueCode;

/** Todos / 未选场馆时的分页缓存 key；有场馆码时按进游场馆码分桶。 */
export const ORO_MX_ALL_GAMES_KEY = 'venue:all';

export const oroMxVenueListKey = (code: string) =>
  code ? `venue:${resolveVenueGameCode(code)}` : ORO_MX_ALL_GAMES_KEY;

export const oroMxVenueListParams = (code: string): GameListParams =>
  code ? { venue: resolveVenueGameCode(code) } : {};

/** 顶栏 Tab + 场馆的分页缓存 key，避免换 Tab / 换场馆串页。 */
export const oroMxPaneListKey = (tabId: string, venueCode: string) =>
  `${tabId}:${oroMxVenueListKey(venueCode)}`;

export const oroMxPaneListParams = (
  tabId: string,
  venueCode: string,
  node?: GameNavNode | null,
): GameListParams => {
  const venue = oroMxVenueListParams(venueCode);
  if (isOroMxChromeTab(tabId) || !node) {
    return venue;
  }
  return { ...listParamsFromNavNode(node), ...venue };
};

export const oroMxGameMatchesVenue = (gameVenueCode: string | undefined, selected: string) => {
  if (!selected) return true;
  if (!gameVenueCode) return false;
  return resolveVenueGameCode(gameVenueCode) === resolveVenueGameCode(selected);
};

/** 按源站 18 家顺序铺场馆条；无上架游戏的场馆不展示。 */
export const displayVenueNodes = (nodes: GameNavNode[]): GameNavNode[] => {
  const byCode = new Map(
    venueNavNodes(nodes).map((node) => [node.refCode.toUpperCase(), node] as const),
  );
  return oroMxVenueCatalog
    .map((venue) => {
      const node = byCode.get(venue.code);
      const aliased = resolveVenueGameCode(venue.code);
      const aliasNode = aliased !== venue.code ? byCode.get(aliased) : undefined;
      return {
        children: [],
        count: node?.count || aliasNode?.count || 0,
        iconUrl: `${oroMxAssetPaths.providerDir}/${venue.file}`,
        key: node?.key ?? `venue-${venue.code.toLowerCase()}`,
        label: node?.label ?? venue.label,
        refCode: venue.code,
        type: 'VENUE' as const,
      };
    })
    .filter((venue) => venue.count > 0);
};

export const oroMxTabIcon = (refCode: string) => {
  const venue = oroMxVenueCatalog.find(
    (item) => item.code === refCode || item.code.toLowerCase() === refCode.toLowerCase(),
  );
  if (venue) {
    return `${oroMxAssetPaths.providerDir}/${venue.file}`;
  }
  const icons: Record<string, string> = {
    live_casino: oroMxAssetPaths.liveCasino,
    nuevos: oroMxAssetPaths.nuevos,
    popular: oroMxAssetPaths.popular,
    'popular-alt': oroMxAssetPaths.popularAlt,
    fishing: oroMxAssetPaths.fishing,
    slot: oroMxAssetPaths.slots,
    sportsbook: oroMxAssetPaths.sports,
  };
  return icons[refCode] || oroMxAssetPaths.popular;
};

export const oroMxNodeIcon = (node: Pick<GameNavNode, 'iconUrl' | 'refCode'>) => {
  const venue = oroMxVenueCatalog.find(
    (item) => item.code === node.refCode || item.code.toLowerCase() === node.refCode.toLowerCase(),
  );
  if (venue) {
    return `${oroMxAssetPaths.providerDir}/${venue.file}`;
  }
  return resolveOroMxPublicUrl(node.iconUrl) || oroMxTabIcon(node.refCode);
};

export const resolveOroMxLobbyTab = (id: string, nodes: GameNavNode[]) => {
  if (!id || isOroMxChromeTab(id)) {
    return id || 'lobby';
  }
  const flat = flattenGameNav(nodes);
  if (flat.some((node) => node.key === id)) {
    return id;
  }
  const alias = TAB_ALIASES[id] ?? id;
  const matched = flat.find((node) => node.refCode === alias || node.refCode === id);
  if (matched) {
    return matched.key;
  }
  return flat.length ? 'lobby' : id;
};

export const oroMxCatalogLabel = (node: Pick<GameNavNode, 'label' | 'refCode'>) => {
  const key = CATALOG_LABEL_KEYS[node.refCode];
  return key ? t(key) : node.label;
};

export const toOroMxCatalogTab = (node: GameNavNode): OroMxTab => ({
  icon: resolveOroMxPublicUrl(node.iconUrl) || oroMxTabIcon(node.refCode),
  iconKind: 'img',
  id: node.key,
  label: oroMxCatalogLabel(node),
});
